import React, { useState, useRef } from "react";
import {
  AlertTriangle,
  RefreshCw,
  Send,
  Save,
  MessageSquare,
  LayoutGrid,
  RotateCcw,
} from "lucide-react";
import Toast from "../Toast/Toast";
import DeveloperCode from "./DeveloperCode";
import MetricSettings from "./MetricSettings";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc,
  serverTimestamp,
  doc,
  setDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { usePlan } from "../../context/PlanContext"; // Add PlanContext
import { useDashboardCustomization } from "../../context/DashboardCustomizationContext"; // Add this import

const Settings = () => {
  const [showFirstConfirm, setShowFirstConfirm] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [suggestion, setSuggestion] = useState("");
  const metricSettingsRef = useRef(null);
  const { user } = useAuth();
  const { isPro } = usePlan();
  const { enterCustomizationMode } = useDashboardCustomization(); // Get the customization function

  // Add function to handle reset to default layout
  const handleResetToDefault = async () => {
    if (!user || !isPro()) return;

    try {
      // Delete the existing customization document
      const customizationRef = doc(db, "dashboardCustomization", user.uid);
      await deleteDoc(customizationRef);

      // Show success message
      setToast({
        type: "success",
        message: "Dashboard Reset",
        subText: "Your dashboard has been reset to default layout",
      });

      // Refresh the page to reload with defaults
      window.location.reload();
    } catch (error) {
      console.error("Error resetting dashboard:", error);
      setToast({
        type: "error",
        message: "Error Resetting Dashboard",
        subText: error.message || "Please try again later",
      });
    }
  };

  // Add function to handle entering customization mode
  const handleEnterCustomizationMode = () => {
    enterCustomizationMode();
    setToast({
      type: "success",
      message: "Customization Mode Activated",
      subText: "Go to Dashboard to customize your layout",
    });
  };

  const clearLocalStorage = () => {
    localStorage.removeItem("trades");
    localStorage.removeItem("accountBalance");
    localStorage.removeItem("dailyPnL");
    window.dispatchEvent(new Event("storage"));
  };

  const handleInitialReset = () => {
    setShowFirstConfirm(true);
  };

  const handleContinue = () => {
    setShowFirstConfirm(false);
    setShowFinalConfirm(true);
  };

  const handleFinalReset = async () => {
    if (!user) {
      setToast({
        type: "error",
        message: "Error Resetting Data",
        subText: "Please sign in to reset your data",
      });
      return;
    }

    try {
      // Query all trades for this user
      const tradesRef = collection(db, "trades");
      const tradesQuery = query(tradesRef, where("userId", "==", user.uid));
      const tradesSnapshot = await getDocs(tradesQuery);

      // Query all journal entries for this user
      const journalRef = collection(db, "journalEntries");
      const journalQuery = query(journalRef, where("userId", "==", user.uid));
      const journalSnapshot = await getDocs(journalQuery);

      // Delete trades and journal entries from Firebase
      const deletions = [
        ...tradesSnapshot.docs.map((doc) => deleteDoc(doc.ref)),
        ...journalSnapshot.docs.map((doc) => deleteDoc(doc.ref)),
      ];

      // Wait for all Firebase deletions to complete
      await Promise.all(deletions);

      // Clear localStorage
      clearLocalStorage();

      setShowFinalConfirm(false);
      setToast({
        type: "success",
        message: "Mastery Reset Successfully",
        subText: "All trade data and journal entries have been cleared",
      });

      // Trigger a custom event for components to refresh
      window.dispatchEvent(new CustomEvent("masteryClear"));
    } catch (error) {
      console.error("Error resetting data:", error);
      setToast({
        type: "error",
        message: "Error Resetting Data",
        subText: "Please try again later",
      });
    }
  };

  const handleSuggestionSubmit = async () => {
    if (!suggestion.trim() || !user) return;

    try {
      await addDoc(collection(db, "suggestions"), {
        text: suggestion.trim(),
        userId: user.uid,
        email: user.email,
        createdAt: serverTimestamp(),
        status: "new",
      });

      setToast({
        type: "success",
        message: "Suggestion Sent!",
        subText: "Thank you for your feedback",
      });

      setSuggestion("");
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      setToast({
        type: "error",
        message: "Error Sending Suggestion",
        subText: "Please try again later",
      });
    }
  };

  const handleSaveMetricSettings = async () => {
    if (!user || !metricSettingsRef.current) return;

    try {
      const settings = metricSettingsRef.current.getSettings();

      // Ensure account count is saved as a number
      if (settings.fundedAccountCount) {
        settings.fundedAccountCount = parseInt(settings.fundedAccountCount, 10);
      }

      // Make sure funded balance is saved as a string
      if (settings.fundedBalance) {
        settings.fundedBalance = String(settings.fundedBalance);
      }

      console.log("Saving settings to Firestore:", settings);

      const settingsDocRef = doc(db, "userSettings", user.uid);
      await setDoc(settingsDocRef, settings);

      // Add console log to verify settings
      console.log("Saved settings:", settings);

      setToast({
        type: "success",
        message: "Settings Saved",
        subText: "Your metrics settings have been updated.",
      });

      // Dispatch multiple events to ensure all components update
      window.dispatchEvent(new Event("settingsUpdated"));
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("tradesUpdated"));
    } catch (error) {
      console.error("Error saving settings:", error);
      setToast({
        type: "error",
        message: "Error Saving Settings",
        subText: "Please try again.",
      });
    }
  };

  const FirstConfirmDialog = () => (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header warning">
          <AlertTriangle size={24} />
          <h2>Reset Mastery Log?</h2>
        </div>
        <p className="modal-message">
          Are you sure you want to reset your Mastery Log? This will remove all
          of your trade history, journal entries, and saved data. This action
          cannot be undone.
        </p>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowFirstConfirm(false)}
          >
            Nevermind
          </button>
          <button className="btn-warning" onClick={handleContinue}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );

  const FinalConfirmDialog = () => (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header danger">
          <AlertTriangle size={24} />
          <h2>Final Warning!</h2>
        </div>
        <p className="modal-message">
          Are you absolutely positive? This will permanently delete ALL of your
          trading history, journal entries, and saved data. This cannot be
          recovered!
        </p>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowFinalConfirm(false)}
          >
            Forget About It
          </button>
          <button className="btn-danger" onClick={handleFinalReset}>
            Reset Mastery
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="settings-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <div className="settings-section">
        <h2>Data Management</h2>
        <div className="settings-content">
          <div className="setting-item">
            <div className="setting-info">
              <h3>Reset Mastery Log</h3>
              <p>
                Remove all trade history, journal entries, and saved data to
                start fresh. This action cannot be undone.
              </p>
            </div>
            <button className="reset-button" onClick={handleInitialReset}>
              <RefreshCw size={16} />
              Reset
            </button>
          </div>

          {/* Dashboard Customization for Pro Users */}
          <div className="setting-item">
            <div className="setting-info">
              <h3>
                Dashboard Layout Customization{" "}
                <span className="pro-badge">PRO</span>
              </h3>
              <p>
                Customize your dashboard by selecting which metrics to display
                and arranging them to your preference.
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              {/* Reset to Default Button */}
              <button
                className="reset-button"
                style={{
                  backgroundColor: isPro() ? "#F59E0B" : "#6b7280", // Orange color
                  cursor: isPro() ? "pointer" : "not-allowed",
                  opacity: isPro() ? 1 : 0.7,
                }}
                disabled={!isPro()}
                title={
                  !isPro()
                    ? "Upgrade to Pro to access this feature"
                    : "Reset dashboard to default layout"
                }
                onClick={handleResetToDefault}
              >
                <RotateCcw size={16} />
                Reset Layout
              </button>

              {/* Customize Button */}
              <button
                className="reset-button"
                style={{
                  backgroundColor: isPro() ? "var(--accent-color)" : "#6b7280",
                  cursor: isPro() ? "pointer" : "not-allowed",
                  opacity: isPro() ? 1 : 0.7,
                }}
                disabled={!isPro()}
                title={!isPro() ? "Upgrade to Pro to access this feature" : ""}
                onClick={handleEnterCustomizationMode}
              >
                <LayoutGrid size={16} />
                Customize
              </button>
            </div>
          </div>

          {/* Message Support Button */}
          <div className="setting-item">
            <div className="setting-info">
              <h3>Message Support [Coming Soon]</h3>
              <p>
                Need help with your account? Contact our support team for
                assistance.
              </p>
            </div>
            <button
              className="reset-button"
              style={{
                backgroundColor: "#6b7280",
                cursor: "not-allowed",
                opacity: 0.7,
              }}
              disabled={true}
              title="Coming soon"
            >
              <MessageSquare size={16} />
              Contact Support
            </button>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="developer-features-grid">
          <div className="developer-feature-item">
            <DeveloperCode />
          </div>
          <div className="developer-feature-item">
            <div className="settings-section">
              <h2>Developer Suggestions</h2>
              <div className="settings-content">
                <div className="developer-code-container">
                  <textarea
                    value={suggestion}
                    onChange={(e) => setSuggestion(e.target.value)}
                    placeholder="Add features you'd like to see added next! Suggestion submissions might contribute to your plan's usage limit."
                    className="developer-code-input"
                  />
                  <button
                    className="developer-code-button"
                    onClick={handleSuggestionSubmit}
                    disabled={!suggestion.trim()}
                  >
                    <Send size={16} />
                    Submit Suggestion
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <h2>Metric Settings</h2>
          <button className="save-button" onClick={handleSaveMetricSettings}>
            <Save size={16} />
            Save Settings
          </button>
        </div>
        <MetricSettings ref={metricSettingsRef} />
      </div>

      {showFirstConfirm && <FirstConfirmDialog />}
      {showFinalConfirm && <FinalConfirmDialog />}
    </div>
  );
};

export default Settings;
